import React from 'react';

import P from '@hero/ui-kit/typography/P';

import { CARE_CODE_LABELS, careReasonText, codeText } from '../constants';
import * as Style from '../style.module.scss';
import { CodeCounts, Reconciliation } from '../types';
import CareTable from './CareTable';

const nonzeroUnits = (counts: CodeCounts) =>
    Object.entries(counts)
        .filter(([, count]) => count !== 0)
        .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));

const unitsList = (counts: CodeCounts): React.ReactNode => {
    const entries = nonzeroUnits(counts);
    return entries.length ? (
        <div>
            {entries.map(([code, count]) => (
                <P key={code} size="small" noDefaultMargin className={Style.wrapText}>
                    {`${code} · ${count} ${count === 1 ? 'unit' : 'units'}`}
                </P>
            ))}
        </div>
    ) : (
        '0 units'
    );
};

const ReconciliationPanel: React.FC<{ reconciliation?: Reconciliation }> = ({ reconciliation }) => {
    const ready = reconciliation?.available ? reconciliation : undefined;
    const minutesOnly = ready !== undefined &&
        (ready.external_units === 'not_applicable' || ready.units_by_view.source_units === null);
    const codes = ready
        ? Array.from(
              new Set(
                  Object.values(ready.units_by_view).flatMap((counts) => Object.keys(counts ?? {}))
              )
          ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        : [];
    const differences: [string, CodeCounts][] = ready
        ? [
              ...(!minutesOnly
                  ? [
                        [
                            'Imported units vs imported activity',
                            ready.source_leg_unexplained ?? {}
                        ] as [string, CodeCounts]
                    ]
                  : []),
              ['Imported activity vs Hero billing', ready.hero_leg_unexplained],
              [
                  'Imported activity without a Hero billing candidate (included above)',
                  ready.no_hero_candidate
              ]
          ]
        : [];
    const differenceRows = differences
        .filter(([, counts]) => nonzeroUnits(counts).length > 0)
        .map(([label, counts]) => [label, unitsList(counts)]);
    const adjustmentRows = ready
        ? Object.entries(ready.hero_leg_explained)
              .filter(([, counts]) => nonzeroUnits(counts).length > 0)
              .map(([cause, counts]) => [careReasonText(cause), unitsList(counts)])
        : [];
    const unmatchedRows: React.ReactNode[][] = [];
    if (ready) {
        const { units, activity } = ready.unmatched;
        if (
            units.applicable !== false &&
            units.units &&
            (units.rows || nonzeroUnits(units.units).length)
        ) {
            unmatchedRows.push([
                'Billed units',
                units.rows === null
                    ? 'Import row count unavailable'
                    : `${units.rows} import ${units.rows === 1 ? 'row' : 'rows'}`,
                unitsList(units.units)
            ]);
        }
        if (activity.rows || activity.patients || nonzeroUnits(activity.units).length) {
            unmatchedRows.push([
                'Activity',
                <div key="counts">
                    <P size="small" noDefaultMargin>
                        {`${activity.rows} import ${activity.rows === 1 ? 'row' : 'rows'}`}
                    </P>
                    <P size="small" noDefaultMargin>
                        {`${activity.patients} ${activity.patients === 1 ? 'patient' : 'patients'}`}
                    </P>
                </div>,
                unitsList(activity.units)
            ]);
        }
    }

    const status = ready
        ? ready.unexplained_lines > 0
            ? `${ready.unexplained_lines} ${ready.unexplained_lines === 1 ? 'comparison needs' : 'comparisons need'} review`
            : ready.compared_lines === 0
              ? 'no comparisons available'
              : 'no unexplained differences'
        : reconciliation
          ? 'unavailable'
          : null;
    const explanations = ready
        ? [
              `${ready.compared_lines} patient/code ${ready.compared_lines === 1 ? 'combination' : 'combinations'} compared. Differences are checked individually, so equal totals can still contain discrepancies.`
          ]
        : reconciliation
          ? [
                ...(!reconciliation.available && reconciliation.missing
                    ? [codeText(reconciliation.missing)]
                    : []),
                reconciliation.explanation
            ]
          : [];

    return (
        <CareTable
            collapsible
            title={`Billing comparison${status ? ` — ${status}` : ''}`}
            columns={
                ready || !reconciliation
                    ? [
                          'Code',
                          ...(!minutesOnly ? ['Imported units'] : []),
                          'Units from activity',
                          'Hero billable units'
                      ]
                    : []
            }
            gridTemplateColumns={`minmax(0, 1.5fr) repeat(${minutesOnly ? 2 : 3}, minmax(0, 1fr))`}
            rows={
                reconciliation
                    ? codes.map((code) => [
                          <div key="code">
                              <strong>{code}</strong>
                              {CARE_CODE_LABELS.has(code) ? (
                                  <P size="small" noDefaultMargin>
                                      {CARE_CODE_LABELS.get(code)}
                                  </P>
                              ) : null}
                          </div>,
                          ...(!minutesOnly ? [ready?.units_by_view.source_units?.[code] ?? 0] : []),
                          ready?.units_by_view.source_activity[code] ?? 0,
                          ready?.units_by_view.hero[code] ?? 0
                      ])
                    : undefined
            }
            explanations={[
                ...explanations,
                ...(minutesOnly
                    ? [
                          ready?.explanation ??
                              'Derived units are not an independent provider validation.'
                      ]
                    : [])
            ]}
        >
            {ready && differenceRows.length + adjustmentRows.length + unmatchedRows.length > 0 ? (
                <div className={Style.comparisonDetails}>
                    {differenceRows.length > 0 ? (
                        <section>
                            <P noDefaultMargin>
                                <strong>Differences needing review</strong>
                            </P>
                            <CareTable
                                columns={['Comparison', 'Unexplained units']}
                                gridTemplateColumns="minmax(0, 2fr) minmax(0, 1fr)"
                                rows={differenceRows}
                            />
                        </section>
                    ) : null}
                    {adjustmentRows.length > 0 ? (
                        <section>
                            <P noDefaultMargin>
                                <strong>Explained adjustments</strong>
                            </P>
                            <CareTable
                                columns={['Reason', 'Units affected']}
                                gridTemplateColumns="minmax(0, 2fr) minmax(0, 1fr)"
                                rows={adjustmentRows}
                            />
                        </section>
                    ) : null}
                    {unmatchedRows.length > 0 ? (
                        <section>
                            <P noDefaultMargin>
                                <strong>Unmatched imports</strong>
                            </P>
                            <P size="small" noDefaultMargin>
                                These import rows could not be matched to a patient and are excluded
                                from the comparison above.
                            </P>
                            <CareTable
                                columns={['Import', 'Unmatched data', 'Units by code']}
                                gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                                rows={unmatchedRows}
                            />
                        </section>
                    ) : null}
                </div>
            ) : null}
        </CareTable>
    );
};

export default ReconciliationPanel;
